// CreditNova Agent Output Schemas
// Base schema that all specialist agents extend

import { z } from 'zod';
import { sourceCitationSchema } from './report.js';

// ─── Base Agent Output ───
export const agentOutputSchema = z.object({
  agentId: z.string(),
  documentTypesAnalyzed: z.array(z.string()),
  flags: z.array(z.object({
    severity: z.enum(['high', 'medium', 'low']),
    description: z.string(),
    sourceDocument: z.string(),
    sourceField: z.string().optional(),
  })),
  confidenceScore: z.number().min(0).max(1),
  dataCompleteness: z.object({
    expectedFields: z.number(),
    populatedFields: z.number(),
    missing: z.array(z.string()),
  }).optional(),
  narrativeContribution: z.string(),
});

export type AgentOutput = z.infer<typeof agentOutputSchema>;

// ─── Financial Analysis Agent ───
export const financialAnalystOutputSchema = agentOutputSchema.extend({
  ratioInterpretations: z.array(z.object({
    ratioName: z.string(),
    category: z.string(),
    calculatedValue: z.string(),
    formula: z.string(),
    calculation: z.string(),
    interpretation: z.string(),
    severity: z.enum(['positive', 'neutral', 'concern', 'critical']).optional(),
    sources: z.array(sourceCitationSchema),
  })),
  anomalies: z.array(z.object({
    description: z.string(),
    severity: z.enum(['high', 'medium', 'low']),
    sourceLineItems: z.array(z.string()),
    suggestedFollowUp: z.string(),
  })),
  financialNarrative: z.string(),
});

export type FinancialAnalystOutput = z.infer<typeof financialAnalystOutputSchema>;

// ─── Banking Agent ───
export const bankingAgentOutputSchema = agentOutputSchema.extend({
  bankingNarrative: z.string(),
  reconciliation: z.object({
    totalDeposits: z.string().nullable(),
    reportedRevenue: z.string().nullable(),
    varianceAmount: z.string().nullable(),
    variancePercent: z.string().nullable(),
    assessment: z.string(),
  }).nullable(),
  nsfAnalysis: z.object({
    count: z.number(),
    clustering: z.string(),
    severity: z.string(),
  }).nullable(),
  cashFlowAssessment: z.object({
    trend: z.string(),
    averageBalance: z.string().nullable(),
    volatility: z.string(),
  }).nullable(),
});

export type BankingAgentOutput = z.infer<typeof bankingAgentOutputSchema>;

// ─── Credit Profile Agent ───
export const creditProfileAgentOutputSchema = agentOutputSchema.extend({
  creditNarrative: z.string(),
  creditReportItems: z.array(z.object({
    category: z.string(),
    itemName: z.string(),
    value: z.string(),
    significance: z.string(),
    riskLevel: z.enum(['positive', 'neutral', 'concern', 'critical']),
    source: sourceCitationSchema,
  })),
  overallCreditAssessment: z.object({
    scoreTier: z.string(),
    paymentBehavior: z.string(),
    utilizationAssessment: z.string(),
    derogatoryAssessment: z.string().nullable(),
  }),
});

export type CreditProfileAgentOutput = z.infer<typeof creditProfileAgentOutputSchema>;

// ─── Corporate & Legal Agent ───
export const corporateAgentOutputSchema = agentOutputSchema.extend({
  corporateNarrative: z.string(),
  accountItems: z.array(z.object({
    fieldName: z.string(),
    value: z.string(),
    relevance: z.string(),
    source: sourceCitationSchema,
  })),
  structureAssessment: z.object({
    incorporationAge: z.string(),
    ownershipConcentration: z.string(),
    directorStability: z.string(),
    registeredEncumbrances: z.string().nullable(),
    redFlags: z.array(z.string()).optional(),
  }),
});

export type CorporateAgentOutput = z.infer<typeof corporateAgentOutputSchema>;

// ─── Cross-Document Verifier ───
export const crossDocVerificationOutputSchema = agentOutputSchema.extend({
  crossChecks: z.array(z.object({
    checkName: z.string(),
    documentsCompared: z.array(z.string()),
    result: z.enum(['pass', 'flag', 'fail']),
    detail: z.string(),
    severity: z.enum(['high', 'medium', 'low']),
    suggestedFollowUp: z.string().optional(),
  })),
  materialInconsistencies: z.array(z.object({
    description: z.string(),
    severity: z.enum(['high', 'medium', 'low']),
    documentsInvolved: z.array(z.string()),
  })),
});

export type CrossDocVerificationOutput = z.infer<typeof crossDocVerificationOutputSchema>;

// ─── Research Agent ───
export const researchAgentOutputSchema = agentOutputSchema.extend({
  adverseMedia: z.array(z.object({
    title: z.string(), // Descriptive header, e.g., "Corporate Name — Adverse Media Screening"
    finding: z.string(),
    source: z.string(),
    sourceUrl: z.string(),
    date: z.string().nullable(),
    severity: z.enum(['high', 'medium', 'low']),
  })),
  businessLegitimacy: z.object({
    hasWebPresence: z.boolean(),
    hasReviews: z.boolean(),
    hasLinkedIn: z.boolean(),
    assessment: z.string(),
  }),
  industryOutlook: z.object({
    direction: z.enum(['growing', 'stable', 'contracting', 'uncertain']),
    summary: z.string(),
    keyTrends: z.array(z.string()),
    regulatoryConsiderations: z.string().nullable(),
  }),
  industryBenchmarks: z.object({
    typicalGrossMargin: z.string().nullable(),
    typicalDebtToEquity: z.string().nullable(),
    typicalCurrentRatio: z.string().nullable(),
    source: z.string().nullable(),
  }).nullable(),
  principalBackground: z.array(z.object({
    name: z.string(),
    findings: z.string(),
    sourceUrls: z.array(z.string()),
  })),
});

export type ResearchAgentOutput = z.infer<typeof researchAgentOutputSchema>;

// ─── Credit Writer Agent ───
export const writeUpAgentOutputSchema = z.object({
  executiveSummary: z.string(),
  riskAssessment: z.object({
    overallRisk: z.enum(['low', 'moderate', 'elevated', 'high']),
    keyStrengths: z.array(z.string()),
    keyConcerns: z.array(z.string()),
    materialFlags: z.array(z.string()),
  }),
});

export type WriteUpAgentOutput = z.infer<typeof writeUpAgentOutputSchema>;

// ─── Findings Commentator ───
export const findingsCommentatorOutputSchema = z.object({
  enhancedCreditReportItems: z.array(z.object({
    category: z.string(),
    itemName: z.string(),
    value: z.string(),
    significance: z.string(),
    source: sourceCitationSchema,
  })),
  enhancedAccountItems: z.array(z.object({
    fieldName: z.string(),
    value: z.string(),
    relevance: z.string(),
    source: sourceCitationSchema,
  })),
});

export type FindingsCommentatorOutput = z.infer<typeof findingsCommentatorOutputSchema>;

// ─── Pipeline Metadata ───
export const agentPipelineMetadataSchema = z.object({
  totalDurationMs: z.number(),
  agentsRun: z.array(z.string()),
  agentsFailed: z.array(z.string()),
  totalTokensUsed: z.number(),
  totalEstimatedCostUsd: z.number(),
});

export type AgentPipelineMetadata = z.infer<typeof agentPipelineMetadataSchema>;
